import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState({});  // {can_view_products: true, ...}
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCxo, setIsCxo] = useState(false);
  const [isCeo, setIsCeo] = useState(false);

  const loadCaps = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me/capabilities");
      setCapabilities(data.capabilities || {});
      setIsAdmin(!!data.is_admin);
      setIsCxo(!!data.is_cxo);
      setIsCeo(!!data.is_ceo);
    } catch {
      setCapabilities({});
      setIsAdmin(false);
      setIsCxo(false);
      setIsCeo(false);
    }
  }, []);

  const loadMe = useCallback(async () => {
    const token = localStorage.getItem("wc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      await loadCaps();
    } catch {
      localStorage.removeItem("wc_token");
    } finally {
      setLoading(false);
    }
  }, [loadCaps]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = (token, userData) => {
    localStorage.setItem("wc_token", token);
    setUser(userData);
    // Fire-and-forget capability fetch (UI shows things gated as soon as data arrives)
    loadCaps();
  };

  const logout = () => {
    localStorage.removeItem("wc_token");
    setUser(null);
    setCapabilities({});
    setIsAdmin(false);
    setIsCxo(false);
  };

  const refresh = loadMe;

  /** True if the user is admin OR a CXO that has the requested capability set. */
  const hasCap = useCallback((cap) => {
    if (isAdmin) return true;
    return !!capabilities[cap];
  }, [isAdmin, capabilities]);

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, refresh, setUser, capabilities, isAdmin, isCxo, isCeo, hasCap }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
